import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { convertKeyNames, snakeCaseObject } from '@edx/frontend-platform/utils';

/**
 * Changes the user's language preference and active session language
 * @param {string} username - The username of the user
 * @param {string} languageCode - The language code (e.g., 'en', 'es-419', 'fr')
 * @returns {Promise} - Resolves when both API calls are complete
 */
export async function changeUserLanguage(username, languageCode) {
  try {
    console.log(`Changing language for user ${username} to ${languageCode}`);

    // First update user preferences
    const processedParams = snakeCaseObject({ pref_lang: languageCode });
    const convertedParams = convertKeyNames(processedParams, {
      pref_lang: 'pref-lang',
    });

    await getAuthenticatedHttpClient()
      .patch(
        `${getConfig().LMS_BASE_URL}/api/user/v1/preferences/${username}`,
        convertedParams,
        {
          headers: { 'Content-Type': 'application/merge-patch+json' },
        }
      );

    // Then update the active session language
    const formData = new FormData();
    formData.append('language', languageCode);

    await getAuthenticatedHttpClient()
      .post(
        `${getConfig().LMS_BASE_URL}/i18n/setlang/`,
        formData,
        {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        }
      );

    console.log('Language change completed successfully');
    return true;
  } catch (error) {
    console.error('Error changing language:', error);
    throw error;
  }
}

/**
 * Get list of available site languages
 * @returns {Array} Array of language objects with code, name, and released status
 */
export const siteLanguageList = [
  {
    code: 'en',
    name: 'English',
    released: true,
  },
  {
    code: 'ar',
    name: 'العربية',
    released: true,
  },
  {
    code: 'es-es',
    name: 'Español (España)',
    released: true,
  },
  {
    code: 'ko-kr',
    name: '한국어 (대한민국)',
    released: true,
  },
  {
    code: 'zh-cn',
    name: '中文 (简体)',
    released: true,
  },
  {
    code: 'ru',
    name: 'Русский',
    released: true,
  },
  {
    code: 'hi',
    name: 'हिंदी',
    released: true,
  },
  {
    code: 'fr',
    name: 'Français',
    released: true,
  },
  {
    code: 'ml',
    name: 'മലയാളം',
    released: true,
  }
];

/**
 * Check if a language code is valid and released
 * @param {string} languageCode - The language code to validate
 * @returns {boolean} - True if the language is valid and released
 */
export function isValidLanguage(languageCode) {
  const language = siteLanguageList.find(lang => lang.code === languageCode);
  return language ? language.released : false;
}